import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useBlogPostStore } from "@/stores/blogPostStore";
import { Trash2, AlertTriangle } from "lucide-react";

interface ModalDeleteBlogPostProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export function ModalDeleteBlogPost({
  isOpen,
  onClose,
  onComplete,
}: ModalDeleteBlogPostProps) {
  const { selectedBlogPost, deleteBlogPost, isDeleting } = useBlogPostStore();

  const handleDelete = async () => {
    if (!selectedBlogPost) return;

    try {
      await deleteBlogPost(selectedBlogPost._id);
      onComplete();
    } catch (error) {
      console.error("Error deleting blog post:", error);
    }
  };

  if (!selectedBlogPost) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Xác nhận xóa blog post
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
            <Trash2 className="w-5 h-5 text-red-600 mt-0.5" />
            <div>
              <p className="font-medium text-gray-900">
                Bạn có chắc chắn muốn xóa blog post này?
              </p>
              <p className="text-sm text-gray-600 mt-1">
                Hành động này không thể hoàn tác.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 p-3 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">
              Thông tin blog post:
            </h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <span className="font-medium">Tiêu đề:</span>{" "}
                {selectedBlogPost.title}
              </p>
              <p>
                <span className="font-medium">Tác giả:</span>{" "}
                {selectedBlogPost?.author?.name || "(Trống)"}
              </p>
              <p>
                <span className="font-medium">Trạng thái:</span>{" "}
                {selectedBlogPost.status}
              </p>
              <p>
                <span className="font-medium">Hiển thị:</span>{" "}
                {selectedBlogPost.isDisplay ? "Có" : "Không"}
              </p>
              <p>
                <span className="font-medium">Lượt xem:</span>{" "}
                {selectedBlogPost.viewCount}
              </p>
              {selectedBlogPost.category &&
                selectedBlogPost.category.length > 0 && (
                  <p>
                    <span className="font-medium">Danh mục:</span>{" "}
                    {selectedBlogPost.category.map((c) => c.name).join(", ")}
                  </p>
                )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isDeleting}>
            Hủy
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isDeleting}
            className="cursor-pointer"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Đang xóa...
              </>
            ) : (
              <>
                <Trash2 className="w-4 h-4 mr-2" />
                Xóa blog post
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
